import { HttpClient } from "@angular/common/http";
import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, RedirectCommand, Router, Routes } from "@angular/router";
import { orgIsActiveGuard, roleGuard } from "@kno2/core/session";
import { IntegrationsService } from "@kno2/data-access/integrations";
import { Message } from "@kno2/data-access/messages";
import { firstValueFrom } from "rxjs";
import { releaseDraftGuard } from "./release-draft.guard";

const useIntegrationDocumentTypes = async () => {
    const integrationsService = inject(IntegrationsService);
    const capabilities = await integrationsService.getCapabilitiesAsync();
    const useIntegrationDocumentTypes = capabilities?.capabilities?.find((c) => c.display === "documentTypeLookup")?.reference.includes("integrations");

    return useIntegrationDocumentTypes;
};

const messageResolver = async (route: ActivatedRouteSnapshot): Promise<Message | RedirectCommand> => {
    const messageId = route.params.id;
    if (messageId === "undefined") return new RedirectCommand(inject(Router).parseUrl("/release"));

    const httpClient = inject(HttpClient);
    const message = await firstValueFrom(httpClient.get<Message>(`/api/messages/${messageId}`));

    return message;
};

export const routes: Routes = [
    {
        path: "release",
        loadComponent: () => import("./release.component").then((m) => m.ReleaseComponent),
        canActivate: [roleGuard],
        data: {
            roles: ["Administrator", "User"]
        }
    },
    {
        path: "release/new",
        pathMatch: "full",
        loadComponent: () => import("./new/release-new.component").then((m) => m.ReleaseNewComponent),
        canActivate: [orgIsActiveGuard, roleGuard],
        canDeactivate: [releaseDraftGuard],
        data: {
            roles: ["Administrator", "User"]
        },
        resolve: {
            useIntegrationDocumentTypes: async () => await useIntegrationDocumentTypes()
        }
    },
    {
        path: "release/:id",
        loadComponent: () => import("./view/release-view.component").then((m) => m.ReleaseViewComponent),
        canActivate: [roleGuard],
        data: {
            roles: ["Administrator", "User"]
        },
        resolve: {
            useIntegrationDocumentTypes: async () => await useIntegrationDocumentTypes(),
            message: async (route: ActivatedRouteSnapshot) => await messageResolver(route)
        }
    },
    {
        path: "release/draft/:id",
        canActivate: [orgIsActiveGuard, roleGuard],
        canDeactivate: [releaseDraftGuard],
        loadComponent: () => import("./new/release-new.component").then((m) => m.ReleaseNewComponent),
        data: {
            roles: ["Administrator", "User"]
        },
        resolve: {
            useIntegrationDocumentTypes: async () => await useIntegrationDocumentTypes(),
            message: async (route: ActivatedRouteSnapshot) => await messageResolver(route)
        }
    },
    {
        path: "release/forward/:id",
        loadComponent: () => import("./new/release-response.component").then((m) => m.ReleaseResponseComponent),
        canActivate: [roleGuard],
        data: {
            roles: ["Administrator", "User"]
        },
        resolve: {
            useIntegrationDocumentTypes: async () => await useIntegrationDocumentTypes(),
            message: async (route: ActivatedRouteSnapshot) => await messageResolver(route)
        }
    },
    {
        path: "release/reply/:id",
        loadComponent: () => import("./new/release-response.component").then((m) => m.ReleaseResponseComponent),
        canActivate: [roleGuard],
        data: {
            roles: ["Administrator", "User"]
        },
        resolve: {
            useIntegrationDocumentTypes: async () => await useIntegrationDocumentTypes(),
            message: async (route: ActivatedRouteSnapshot) => await messageResolver(route)
        }
    }
];
